import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { CREATIVE_EXPRESSIONS, findExpression } from "@/data/creative-expressions";
import type { CreativeExpression } from "@/data/creative-expressions";
import { FeelItDrawIt } from "@/components/creative-expression/FeelItDrawIt";
import { SelfLoveReflection } from "@/components/creative-expression/SelfLoveReflection";
import { SelfCareWheel } from "@/components/creative-expression/SelfCareWheel";
import { ChangeMap } from "@/components/creative-expression/ChangeMap";
import { CompassionMirror } from "@/components/creative-expression/CompassionMirror";
import { EmotionalIceberg } from "@/components/creative-expression/EmotionalIceberg";
import { FutureSelf } from "@/components/creative-expression/FutureSelf";
import { FillMyCup } from "@/components/creative-expression/FillMyCup";
import { WorryJar } from "@/components/creative-expression/WorryJar";
import { LetItGo } from "@/components/creative-expression/LetItGo";
import { SavedExpressions } from "@/components/creative-expression/SavedExpressions";
import { useScrollTopOnChange } from "@/hooks/use-scroll-top-on-change";

export const Route = createFileRoute("/creative-expression")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Creative Expression Experiences — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Ten gentle, colourful guided experiences — draw, reflect and release. Private to your account, at your own pace.",
      },
      { property: "og:title", content: "Creative Expression Experiences — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Ten gentle, colourful guided experiences — draw, reflect and release. Private to your account, at your own pace.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/creative-expression" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/creative-expression" }],
  }),
  validateSearch: (search: Record<string, unknown>): { e?: string } => ({
    e: typeof search.e === "string" && search.e.length > 0 ? search.e : undefined,
  }),
  component: withMemberGate(CreativeExpressionPage, "Creative Expression"),
});

function CreativeExpressionPage() {
  const { e } = Route.useSearch();
  useScrollTopOnChange(e ?? null);
  const exercise = findExpression(e);
  const navigate = useNavigate();

  if (exercise) return <ExerciseView exercise={exercise} onBack={() => void navigate({ to: "/creative-expression" })} />;

  return <Hub />;
}

function ExerciseView({ exercise, onBack }: { exercise: CreativeExpression; onBack: () => void }) {
  switch (exercise.slug) {
    case "feel-it-draw-it":
      return <FeelItDrawIt exercise={exercise} onBack={onBack} />;
    case "self-love-reflection":
      return <SelfLoveReflection exercise={exercise} onBack={onBack} />;
    case "self-care-wheel":
      return <SelfCareWheel exercise={exercise} onBack={onBack} />;
    case "change-map":
      return <ChangeMap exercise={exercise} onBack={onBack} />;
    case "compassion-mirror":
      return <CompassionMirror exercise={exercise} onBack={onBack} />;
    case "emotional-iceberg":
      return <EmotionalIceberg exercise={exercise} onBack={onBack} />;
    case "future-self":
      return <FutureSelf exercise={exercise} onBack={onBack} />;
    case "fills-my-cup":
      return <FillMyCup exercise={exercise} onBack={onBack} />;
    case "worry-jar":
      return <WorryJar exercise={exercise} onBack={onBack} />;
    case "let-it-go":
      return <LetItGo exercise={exercise} onBack={onBack} />;
    default:
      return <Hub />;
  }
}

function Hub() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:py-12">
      <header className="text-center">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[var(--gold-deep)]">
          🌈 Creative Expression
        </p>
        <h1 className="mt-3 font-display text-3xl text-foreground sm:text-4xl md:text-5xl">
          Ten little worlds to feel, draw and let go
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base text-muted-foreground">
          Guided, colourful experiences you move through at your own pace.
          Your saved writing and drawings stay private. Voice notes are never uploaded.
        </p>
        <p className="mx-auto mt-3 max-w-xl text-xs text-muted-foreground/80">
          May help support calm, self-reflection and wellbeing. These are
          creative wellbeing experiences, not therapy or clinical intervention.
        </p>
      </header>

      <section className="mt-10">
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CREATIVE_EXPRESSIONS.map((ex, i) => (
            <li key={ex.slug} className="ce-anim-pop" style={{ animationDelay: `${i * 60}ms` }}>
              <Link
                to="/creative-expression"
                search={{ e: ex.slug }}
                className="group flex h-full w-full flex-col rounded-3xl border border-border/40 p-5 text-start shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                style={{ background: ex.gradient }}
                aria-label={`${ex.title} — start experience`}
              >
                <span aria-hidden="true" className="ce-anim-float text-4xl">
                  {ex.emoji}
                </span>
                <h2 className="mt-3 font-display text-xl text-foreground">{ex.title}</h2>
                <p className="mt-1 text-sm text-foreground/75">{ex.tagline}</p>
                <span className="mt-auto pt-4 text-[11px] font-semibold uppercase tracking-wider text-foreground/60">
                  {ex.minutes}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <SavedExpressions />

      <section className="mt-12 rounded-3xl border border-gold/30 bg-cream/40 p-6 text-center md:p-8">
        <p className="mx-auto max-w-xl text-sm text-muted-foreground">
          Prefer a blank page? You can also open the sketchbook without a
          prompt and draw whatever comes.
        </p>
        <Link
          to="/art-board"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs font-semibold text-[var(--gold-deep)] transition hover:bg-gold/10"
        >
          Open blank canvas
        </Link>
        <div className="mt-6">
          <Link
            to="/creative-reflection"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)]"
          >
            ← Back to Creative Reflection
          </Link>
        </div>
      </section>
    </div>
  );
}
